import React, { useState, useMemo } from 'react'
import clsx from 'clsx'
import AppDateInput from '../../../components/AppDateInput'
import AppTimeInput from '../../../components/AppTimeInput'
import {
  toDateKey,
  parseDate,
  parseNaiveDate,
  getJobStatusColorClass,
} from '../allocationUtils'
import { useGetEmployeeAvailabilityForRangeQuery } from '../../../api/serverStateHooks'

function extractTime(iso, defaultTime = '09:00') {
  if (!iso) return defaultTime
  const d = parseNaiveDate(iso)
  if (!d || isNaN(d.getTime())) return defaultTime
  const h = d.getHours()
  const m = d.getMinutes()
  if (h === 0 && m === 0) return defaultTime
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
}

export default function JobAssignmentModal({
  employee,
  job,
  month,
  initialDate,
  slotTimes = null,
  jobPool = [],
  onAssign,
  onClose,
  isAssigning = false,
  assignWarnings = [],
  isUpdate = false,
  onRefresh,
  isRefreshing = false,
}) {
  const [selectedJobId, setSelectedJobId] = useState(() => job?.job_id || job?.id || '')

  const [startDate, setStartDate] = useState(() => {
    if (job?.planned_start) return toDateKey(parseDate(job.planned_start))
    return initialDate || toDateKey(new Date())
  })
  const [endDate, setEndDate] = useState(() => {
    if (job?.planned_end) return toDateKey(parseDate(job.planned_end))
    if (job?.planned_start) return toDateKey(parseDate(job.planned_start))
    return initialDate || toDateKey(new Date())
  })
  const [startTime, setStartTime] = useState(() => {
    if (slotTimes?.startTime) return slotTimes.startTime
    if (job?.planned_start) return extractTime(job.planned_start, '09:00')
    if (employee?.shift_intime) return employee.shift_intime.slice(0, 5)
    return '09:00'
  })
  const [endTime, setEndTime] = useState(() => {
    if (slotTimes?.endTime) return slotTimes.endTime
    if (job?.planned_end) return extractTime(job.planned_end, '18:00')
    if (employee?.shift_outtime) return employee.shift_outtime.slice(0, 5)
    return '18:00'
  })

  const [plannedHours, setPlannedHours] = useState(() => {
    if (job?.planned_hours != null) return String(job.planned_hours)
    if (slotTimes?.free_hours != null) return String(slotTimes.free_hours)
    if (slotTimes?.hours != null) return String(slotTimes.hours)
    if (employee?.base_hours_per_day != null) return String(employee.base_hours_per_day)
    return '8'
  })
  const [confirmWarnings, setConfirmWarnings] = useState(false)

  const activeJob = job || (jobPool || []).find((j) => String(j.job_id || j.id) === String(selectedJobId))

  // Fetch employee availability for the chosen dates
  const queryStart = startDate || `${month}-01`
  const queryEnd = endDate || startDate || `${month}-28`

  const { data: availData, isLoading: isAvailLoading } = useGetEmployeeAvailabilityForRangeQuery(
    {
      employee_id: employee?.employee_id,
      start_date: queryStart,
      end_date: queryEnd,
      exclude_job_id: isUpdate && activeJob?.job_id ? activeJob.job_id : undefined,
    },
    { skip: !employee?.employee_id },
  )

  const availMap = useMemo(() => {
    const map = {}
    const rows = Array.isArray(availData) ? availData : []
    rows.forEach((r) => {
      const key = r.date || r.availability_date
      if (key) {
        map[key] = {
          free: parseFloat(r.free_hours || 0),
          base: parseFloat(r.base_hours || employee?.base_hours_per_day || 8),
          dayType: r.day_type || 'WORKING',
        }
      }
    })
    return map
  }, [availData, employee])

  const handleStartTimeChange = (val) => {
    setStartTime(val)
    if (startDate === endDate && val && endTime) {
      const [sh, sm] = val.split(':').map(Number)
      const [eh, em] = endTime.split(':').map(Number)
      const diff = (eh + em / 60) - (sh + sm / 60)
      if (diff > 0) {
        setPlannedHours(String(Math.round(diff * 10) / 10))
      }
    }
  }

  const handleEndTimeChange = (val) => {
    setEndTime(val)
    if (startDate === endDate && startTime && val) {
      const [sh, sm] = startTime.split(':').map(Number)
      const [eh, em] = val.split(':').map(Number)
      const diff = (eh + em / 60) - (sh + sm / 60)
      if (diff > 0) {
        setPlannedHours(String(Math.round(diff * 10) / 10))
      }
    }
  }

  const handleSubmit = (ignoreWarnings = false) => {
    if (!startDate || !activeJob) return
    const sTime = startTime && /^\d{2}:\d{2}/.test(startTime)
      ? (startTime.length === 5 ? `${startTime}:00` : startTime)
      : '09:00:00'
    const eTime = endTime && /^\d{2}:\d{2}/.test(endTime)
      ? (endTime.length === 5 ? `${endTime}:00` : endTime)
      : '18:00:00'

    const payload = {
      job_id: activeJob.job_id || activeJob.id,
      employee_id: employee.employee_id,
      planned_start: `${startDate}T${sTime}`,
      planned_end: endDate ? `${endDate}T${eTime}` : `${startDate}T${eTime}`,
      planned_hours: plannedHours ? parseFloat(plannedHours) : null,
      ignore_warnings: ignoreWarnings || confirmWarnings,
    }
    onAssign(payload)
  }

  const hasWarnings = assignWarnings && assignWarnings.length > 0

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm animate-fadeIn">
      <div className="bg-surface rounded-2xl shadow-2xl border border-outline-variant/30 max-w-xl w-full overflow-hidden flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="px-6 py-5 border-b border-outline-variant/20 flex items-center justify-between bg-surface-container-low">
          <div>
            <div className="flex items-center gap-2">
              <span className="inline-block w-2.5 h-2.5 rounded-full bg-blue-600" />
              <h2 className="text-lg font-headline font-bold text-on-surface">
                {isUpdate ? 'Update Job Allocation' : 'Assign Job'}
              </h2>
            </div>
            <p className="text-xs text-outline mt-0.5">
              Real Availability (Committed) &bull; Based on Jobs
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-outline hover:text-on-surface p-1 rounded-lg hover:bg-surface-container transition-colors cursor-pointer"
          >
            <span className="material-symbols-outlined text-xl">close</span>
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-6 space-y-5 overflow-y-auto flex-1">
          {/* Target Employee Info */}
          <div className="flex items-center justify-between p-3.5 rounded-xl bg-surface-container-low border border-outline-variant/20">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-primary/10 text-primary font-bold flex items-center justify-center text-sm">
                {(employee?.name || employee?.employee_name)?.charAt(0) || 'E'}
              </div>
              <div>
                <h4 className="font-bold text-sm text-on-surface">{employee?.name || employee?.employee_name}</h4>
                <p className="text-xs text-outline">{employee?.designation || employee?.role_name || 'Team Member'}</p>
              </div>
            </div>
            <div className="text-right flex flex-col items-end gap-1">
              <span className="text-xs font-semibold text-primary bg-primary/10 px-2.5 py-1 rounded-lg">
                {employee?.base_hours_per_day || 8}h / day
              </span>
              {employee?.shift_name && (
                <span className="text-[10px] text-outline font-medium">
                  {employee.shift_name} ({employee.shift_intime?.slice(0, 5)} – {employee.shift_outtime?.slice(0, 5)})
                </span>
              )}
            </div>
          </div>

          {/* Available Slot Cue */}
          {slotTimes && (
            <div className="flex items-center gap-2 p-3 rounded-xl bg-primary/5 border border-dashed border-primary/40 text-primary text-xs font-semibold">
              <span className="material-symbols-outlined text-sm">schedule</span>
              <span>Allocating into Available Slot: {slotTimes.startTime} – {slotTimes.endTime} ({slotTimes.free_hours || slotTimes.hours}h free)</span>
            </div>
          )}

          {/* Job Info or Pool Selector */}
          {activeJob ? (
            <div className="p-4 rounded-xl bg-surface-container-lowest border border-outline-variant/30">
              <div className="flex items-start justify-between gap-2 mb-2">
                <h3 className="font-bold text-base text-on-surface leading-tight">
                  {activeJob?.job_name || activeJob?.title || `Job #${activeJob?.job_id || activeJob?.id}`}
                </h3>
                <span
                  className={clsx(
                    'text-[10px] font-bold px-2 py-0.5 rounded-full shrink-0',
                    getJobStatusColorClass(activeJob?.status),
                  )}
                >
                  {activeJob?.status || 'DRAFT'}
                </span>
              </div>
              {activeJob?.task_name && (
                <p className="text-xs text-on-surface-variant">
                  <span className="text-outline font-medium">Task:</span> {activeJob.task_name}
                </p>
              )}
              {activeJob?.project_name && (
                <p className="text-xs text-on-surface-variant mt-0.5">
                  <span className="text-outline font-medium">Project:</span> {activeJob.project_name}
                </p>
              )}
              {!job && (
                <button
                  type="button"
                  onClick={() => setSelectedJobId('')}
                  className="mt-2 text-xs text-primary font-semibold hover:underline flex items-center gap-1 cursor-pointer"
                >
                  <span className="material-symbols-outlined text-xs">change_circle</span>
                  <span>Select different job from pool</span>
                </button>
              )}
            </div>
          ) : (
            <div className="p-4 rounded-xl bg-surface-container-lowest border border-outline-variant/30 space-y-2">
              <label className="block text-xs font-bold text-on-surface-variant uppercase tracking-wider">
                Select Unassigned Job from Pool
              </label>
              <select
                value={selectedJobId}
                onChange={(e) => {
                  setSelectedJobId(e.target.value)
                  const found = (jobPool || []).find((j) => String(j.job_id || j.id) === String(e.target.value))
                  if (found?.planned_hours != null) {
                    setPlannedHours(String(found.planned_hours))
                  }
                }}
                className="w-full px-3 py-2 text-sm bg-surface-container-low border border-outline-variant/30 rounded-lg text-on-surface focus:outline-none focus:border-primary transition-colors cursor-pointer"
              >
                <option value="">-- Choose unassigned job to assign --</option>
                {(jobPool || []).map((j) => (
                  <option key={j.job_id || j.id} value={j.job_id || j.id}>
                    {j.job_name || j.title} ({j.project_name || 'Project'}) — {j.planned_hours || 1}h
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Schedule: Start Date & Time */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold text-on-surface-variant mb-1.5 uppercase tracking-wider">
                Planned Start Date
              </label>
              <AppDateInput
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-on-surface-variant mb-1.5 uppercase tracking-wider">
                Planned Start Time
              </label>
              <AppTimeInput
                value={startTime}
                onChange={handleStartTimeChange}
                className="w-full"
                inputClassName="w-full text-sm font-semibold"
                placeholder="09:00 AM"
              />
            </div>
          </div>

          {/* Schedule: End Date & Time */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold text-on-surface-variant mb-1.5 uppercase tracking-wider">
                Planned End Date
              </label>
              <AppDateInput
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                min={startDate}
                className="w-full"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-on-surface-variant mb-1.5 uppercase tracking-wider">
                Planned End Time
              </label>
              <AppTimeInput
                value={endTime}
                onChange={handleEndTimeChange}
                className="w-full"
                inputClassName="w-full text-sm font-semibold"
                placeholder="06:00 PM"
              />
            </div>
          </div>

          {/* Planned Hours */}
          <div>
            <label className="block text-xs font-bold text-on-surface-variant mb-1.5 uppercase tracking-wider">
              Planned Hours
            </label>
            <input
              type="number"
              step="0.5"
              min="0"
              value={plannedHours}
              onChange={(e) => setPlannedHours(e.target.value)}
              className="w-full px-3 py-2 text-sm bg-surface-container-low border border-outline-variant/30 rounded-lg text-on-surface focus:outline-none focus:border-primary transition-colors"
              placeholder="e.g. 8"
            />
          </div>

          {/* Warnings Banner */}
          {hasWarnings && (
            <div className="p-4 rounded-xl bg-amber-50 border border-amber-300 text-amber-900 space-y-2">
              <div className="flex items-center gap-2 font-bold text-xs uppercase tracking-wider text-amber-800">
                <span className="material-symbols-outlined text-sm">warning</span>
                Capacity Warning
              </div>
              <ul className="text-xs list-disc list-inside space-y-1">
                {assignWarnings.map((warn, i) => (
                  <li key={i}>{warn}</li>
                ))}
              </ul>
              <label className="flex items-center gap-2 text-xs font-semibold text-amber-950 pt-1 cursor-pointer">
                <input
                  type="checkbox"
                  checked={confirmWarnings}
                  onChange={(e) => setConfirmWarnings(e.target.checked)}
                  className="rounded border-amber-400 text-amber-600 focus:ring-amber-500"
                />
                I understand this will cause weekly overallocation. Proceed anyway.
              </label>
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="px-6 py-4 border-t border-outline-variant/20 flex items-center justify-between bg-surface-container-low">
          {onRefresh && (
            <button
              type="button"
              onClick={onRefresh}
              disabled={isRefreshing}
              className="flex items-center gap-1.5 text-xs font-semibold text-on-surface-variant hover:text-primary transition-colors cursor-pointer"
            >
              <span className={clsx('material-symbols-outlined text-sm', isRefreshing && 'animate-spin')}>
                refresh
              </span>
              <span>Re-check Capacity</span>
            </button>
          )}
          <div className="flex items-center gap-3 ml-auto">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-xs font-bold text-on-surface-variant hover:text-on-surface bg-surface hover:bg-surface-container-high rounded-xl border border-outline-variant/30 transition-all cursor-pointer"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => handleSubmit(false)}
              disabled={isAssigning || !startDate || !activeJob}
              className="px-5 py-2 text-xs font-bold text-white bg-primary hover:bg-primary/90 rounded-xl transition-all shadow-sm active:scale-95 disabled:opacity-50 cursor-pointer flex items-center gap-2"
            >
              {isAssigning && (
                <span className="material-symbols-outlined text-sm animate-spin">progress_activity</span>
              )}
              <span>{isUpdate ? 'Update Allocation' : 'Assign Job'}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
